import { useMemo } from 'react';

import { AutoCompleteOption } from '../types/AutoCompleteOption';
import { ClientTeamMemberBase } from '../types/ClientTeamMember';
import { filterClientTeamMembers } from '../utils/filterClientTeamMembers';

/**
 * Hook to filter client team members based on a search query
 *
 * This hook provides memoized filtering of guest options, ensuring that
 * filtering only re-runs when the guests array or query changes.
 *
 * @template T Type extending ClientTeamMemberBase
 * @param guests Array of wrapped guest options to filter
 * @param query Search query string (case-insensitive)
 * @returns Memoized filtered array maintaining the wrapper structure
 *
 * @example
 * ```typescript
 * const MyComponent = () => {
 *   const [query, setQuery] = useState("");
 *   const wrappedGuests = guests.map(g => ({ value: g }));
 *   const filteredGuests = useClientTeamMemberFilter(wrappedGuests, query);
 *
 *   return (
 *     <AutoComplete
 *       data={filteredGuests}
 *       onQueryChange={setQuery}
 *       ...
 *     />
 *   );
 * };
 * ```
 */
export function useClientTeamMemberFilter<T extends ClientTeamMemberBase>(
  guests: Array<AutoCompleteOption<T>>,
  query: string,
): Array<AutoCompleteOption<T>> {
  return useMemo(() => filterClientTeamMembers(guests, query), [guests, query]);
}
